import React from 'react';
import { createDisplayName } from '@withjoy/joykit/utils';
import { useOverrides } from '@shared/utils/overrides';
import { useStyleConfig } from '../../common/utils/styleConfig';
import type { BadgeProps } from './Badge.types';
import { badgeStyles, StyledRoot, StyledIcon, StyledLabel, BADGE_DEFAULT_PROPS } from './Badge.styles';

const Badge = React.memo(
  React.forwardRef<HTMLDivElement, BadgeProps>(
    ({ variant = BADGE_DEFAULT_PROPS.variant, intent = BADGE_DEFAULT_PROPS.intent, size = BADGE_DEFAULT_PROPS.size, count, icon, overrides = {}, children, ...boxProps }, ref) => {
      const isCounter = variant === 'counter';
      const isSingleDigitCounter = isCounter && typeof count === 'number' && count >= 0 && count < 10;
      const displayContent = isCounter ? (children ?? count) : children;
      const hasIcon = icon != null;

      const styles = useStyleConfig(badgeStyles, { ...BADGE_DEFAULT_PROPS, variant, intent, size, isSingleDigitCounter, hasIcon });

      const {
        Root: [Root, rootProps],
        Icon: [Icon, iconProps],
        Label: [Label, labelProps],
      } = useOverrides({ Root: StyledRoot, Icon: StyledIcon, Label: StyledLabel }, overrides);

      return (
        <Root
          ref={ref}
          __css={styles.root}
          {...rootProps}
          {...boxProps}
        >
          {hasIcon && (
            <Icon
              __css={styles.icon}
              {...iconProps}
            >
              {icon}
            </Icon>
          )}
          <Label
            __css={styles.label}
            {...labelProps}
          >
            {displayContent}
          </Label>
        </Root>
      );
    },
  ),
);

Badge.displayName = createDisplayName('Badge');

export { Badge };
